// ============================================================================
// CHANGO V5.0 — Process Library (Task #119 / Round 12)
// ============================================================================
// Manage manufacturing processes (工序库) — list, create, edit, archive.
// Mount: /app/factory/processes
//
// Fields: code, name, name_en, standard_time_min, hourly_rate_rmb,
//         equipment_cost_per_hour, notes
//
// boss/finance only
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, HttpError } from '../../api/client.js';
import ExcelImportModal from '../../components/ExcelImportModal.jsx';
import { useAuth } from '../../hooks/useAuth.js';

export default function ProcessLibrary() {
  const { user } = useAuth() || {};
  const isSales = user?.role === 'sales';

  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Modal form
  const [form, setForm] = useState({
    code: '',
    name: '',
    name_en: '',
    standard_time_min: '',
    hourly_rate_rmb: '',
    equipment_cost_per_hour: '',
    notes: ''
  });

  const loadProcesses = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/api/processes');
      setProcesses(res?.items || []);
    } catch (err) {
      if (!(err instanceof HttpError && err.status === 404)) {
        setError(err.message || '工序加载失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch processes on mount
  useEffect(() => {
    loadProcesses();
  }, []);

  // Check sales role
  if (isSales) {
    return (
      <div className="px-6 py-12 max-w-4xl mx-auto">
        <div className="rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
          权限不足：仅老板和财务可访问工厂管理页面
        </div>
      </div>
    );
  }

  const handleCreateClick = () => {
    setForm({
      code: '',
      name: '',
      name_en: '',
      standard_time_min: '',
      hourly_rate_rmb: '',
      equipment_cost_per_hour: '',
      notes: ''
    });
    setEditingId(null);
    setShowModal(true);
  };

  const handleEditClick = (proc) => {
    setForm({
      code: proc.code,
      name: proc.name,
      name_en: proc.name_en || '',
      standard_time_min: proc.standard_time_min ?? '',
      hourly_rate_rmb: proc.hourly_rate_rmb ?? '',
      equipment_cost_per_hour: proc.equipment_cost_per_hour ?? '',
      notes: proc.notes || ''
    });
    setEditingId(proc.id);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setForm({
      code: '',
      name: '',
      name_en: '',
      standard_time_min: '',
      hourly_rate_rmb: '',
      equipment_cost_per_hour: '',
      notes: ''
    });
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      setError('请填写工序名称');
      return;
    }

    if (!editingId && !form.code.trim()) {
      setError('请填写工序编码');
      return;
    }

    try {
      setError(null);
      const payload = {
        name: form.name.trim(),
        name_en: form.name_en.trim() || null,
        standard_time_min: form.standard_time_min ? Number(form.standard_time_min) : null,
        hourly_rate_rmb: form.hourly_rate_rmb ? Number(form.hourly_rate_rmb) : null,
        equipment_cost_per_hour: form.equipment_cost_per_hour ? Number(form.equipment_cost_per_hour) : null,
        notes: form.notes.trim() || null
      };

      if (editingId) {
        await api.patch(`/api/processes/${editingId}`, payload);
      } else {
        await api.post('/api/processes', {
          code: form.code.trim(),
          ...payload
        });
      }

      await loadProcesses();
      handleModalClose();
    } catch (err) {
      setError(err.message || '工序保存失败');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确认归档这个工序？已被产品工艺路线引用的工序不能归档。')) {
      return;
    }

    try {
      await api.delete(`/api/processes/${id}`);
      await loadProcesses();
    } catch (err) {
      setError(err.message || '工序归档失败');
    }
  };

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">工序库</h1>
          <p className="text-sm text-slate-500 mt-1">
            管理制造工序（加工、组装、检测等）
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImportModal(true)}
            className="btn-ghost"
          >
            📥 批量导入
          </button>
          <button
            onClick={handleCreateClick}
            className="btn-primary"
          >
            + 新增工序
          </button>
        </div>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-800">
          {error}
        </div>
      )}

      <div className="card overflow-hidden">
        {loading && (
          <div className="px-4 py-8 text-center text-slate-400">正在加载...</div>
        )}

        {!loading && processes.length === 0 && (
          <div className="px-4 py-8 text-center text-slate-400">
            暂无工序 · <button onClick={handleCreateClick} className="text-chango-700 hover:underline">新增工序</button>
          </div>
        )}

        {!loading && processes.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">工序编码</th>
                <th className="px-4 py-3">工序名称</th>
                <th className="px-4 py-3">标准工时（分钟）</th>
                <th className="px-4 py-3 text-right">人工费率（元/小时）</th>
                <th className="px-4 py-3 text-right">设备能耗（元/小时）</th>
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {processes.map((proc) => (
                <tr key={proc.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-xs">{proc.code}</td>
                  <td className="px-4 py-3">{proc.name}</td>
                  <td className="px-4 py-3 text-sm">
                    {proc.standard_time_min != null ? `${proc.standard_time_min}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-right text-sm">
                    {proc.hourly_rate_rmb != null ? `¥${Number(proc.hourly_rate_rmb).toFixed(2)}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-right text-sm">
                    {proc.equipment_cost_per_hour != null ? `¥${Number(proc.equipment_cost_per_hour).toFixed(2)}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleEditClick(proc)}
                      className="text-xs text-chango-700 hover:underline mr-3"
                    >
                      编辑
                    </button>
                    <button
                      onClick={() => handleDelete(proc.id)}
                      className="text-xs text-rose-600 hover:underline"
                    >
                      归档
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-4">
        <Link to="/app/dashboard" className="text-sm text-slate-600 hover:underline">
          返回工作台
        </Link>
      </div>

      {showImportModal && (
        <ExcelImportModal
          entity="processes"
          onComplete={() => {
            setShowImportModal(false);
            loadProcesses();
          }}
          onClose={() => setShowImportModal(false)}
        />
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4 max-h-96 overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">
              {editingId ? '编辑工序' : '新增工序'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-3">
              {!editingId && (
                <label className="text-xs block">
                  工序编码 *
                  <input
                    type="text"
                    className="field mt-1"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="例如：喷漆、weld-01"
                    maxLength="40"
                  />
                </label>
              )}

              <label className="text-xs block">
                工序名称 *
                <input
                  type="text"
                  className="field mt-1"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="例如：喷漆"
                  maxLength="100"
                />
              </label>

              <label className="text-xs block">
                英文名称
                <input
                  type="text"
                  className="field mt-1"
                  value={form.name_en}
                  onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                  placeholder="例如：Milling"
                  maxLength="100"
                />
              </label>

              <label className="text-xs block">
                标准工时（分钟）
                <input
                  type="number"
                  className="field mt-1"
                  value={form.standard_time_min}
                  onChange={(e) => setForm({ ...form, standard_time_min: e.target.value })}
                  placeholder="0"
                  step="0.1"
                  min="0"
                />
              </label>

              <label className="text-xs block">
                人工费率（元/小时）
                <input
                  type="number"
                  className="field mt-1"
                  value={form.hourly_rate_rmb}
                  onChange={(e) => setForm({ ...form, hourly_rate_rmb: e.target.value })}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </label>

              <label className="text-xs block">
                设备能耗（元/小时）
                <input
                  type="number"
                  className="field mt-1"
                  value={form.equipment_cost_per_hour}
                  onChange={(e) => setForm({ ...form, equipment_cost_per_hour: e.target.value })}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </label>

              <label className="text-xs block">
                备注
                <textarea
                  className="field mt-1"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="内部备注..."
                  rows="2"
                  maxLength="500"
                />
              </label>

              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={handleModalClose}
                  className="btn-ghost text-sm"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="btn-primary text-sm"
                >
                  {editingId ? '保存' : '创建'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
